import { useCallback, useMemo, useState } from 'react';
import { TimekeeperService } from 'services/TimekeeperService';
import { AccrualDate } from 'repository/timekeeper/@types/AccrualDate';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const timekeeperService = new TimekeeperService();

const formatDateByBrowserLocale = (value?: string | Date | null) => {
    if (!value) {
        return '';
    }

    const browserLocale = typeof navigator !== 'undefined' ? navigator.language : undefined;

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return '';
        }
        return dayjs(value)
            .locale(browserLocale as unknown as string)
            .format('L');
    }

    // Parse YYYY-MM-DD as local datetime to avoid UTC timezone shifts.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return dayjs(`${value}T00:00:00`)
            .locale(browserLocale as unknown as string)
            .format('L');
    }

    const parsedDate = dayjs(value);
    if (!parsedDate.isValid()) {
        return value;
    }

    return parsedDate.locale(browserLocale as unknown as string).format('L');
};

export const useAccrualDatesQuery = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<AccrualDate[]>([]);

    const type_date = useMemo(
        () => ({
            fecha_establecimiento: t('accruals.accrual_dates.dialog.inputs.types_date_selectable.establishment_date'),
            fecha_especifica_persona: t('accruals.accrual_dates.dialog.inputs.types_date_selectable.specific_date_person'),
        }),
        [t],
    );

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const data = await timekeeperService.accrual_date.getAll();
            const accrual_dates = data.map((accrual_date) => ({
                id: accrual_date.accrual_date_id,
                accrual_date_id: accrual_date.accrual_date_id,
                name: accrual_date.name,
                date_local_format: formatDateByBrowserLocale(accrual_date.date),
                date: accrual_date.date,
                type_date: type_date[accrual_date.type_date.toLowerCase() as keyof typeof type_date] || accrual_date.type_date,
                date_person_id: accrual_date.date_person_id,
            }));
            const orderedData = accrual_dates.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
            setRows(orderedData as AccrualDate[]);
        } catch (error) {
            console.error('Error loading accrual dates:', error);
            setLoading(false);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [type_date]);

    return { loading, setRows, rows, setLoading, loadData };
};
